import { EventEmitter, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs/Observable';
import { ChannelEvents } from '../../../shared/models/common/channels/channelEvents';
import { EventService } from '../../../shared/services/event/event.service';
import { LeadService } from '../../../shared/services/lead/lead.service';
import { StylesProvider } from '../../../shared/services/utils/styles.provider';
import { ToastService } from '../../../shared/services/utils/toast.service';
import { ValidatePhoneOrEmail } from '../../../shared/validator/email-phone.validator';
import { PlateFormValidator } from '../../../shared/validator/plate-form.validator';
import { LeadsComunicationService } from '../../leads/leads-comunication.service';
import { ModalsHandlerService } from '../../modals/modals.handler.service';
import { LeadInput } from './../../../shared/models/lead/leadInput';
import { SpanishIdValidator } from '../../../shared/validator/nif-cif-nie.validator';

export abstract class TemplateLeadFormComponent implements OnInit {

  @ViewChild('content', { static: false }) content: Element;
  userDropDown: any;
  lead: LeadInput = new LeadInput();
  brandSelected: any;
  channelSelected: any;
  dealerSelected: any;
  typeSelected: any;
  modalEmitter: EventEmitter<any>;
  originAutocomplete: any;
  originSelected: any;
  leadForm: any;
  JSON: JSON;
  sending = false;
  POSTAL_CODE_COUNTRY = 'es';
  zipCode: any;
  simulation = false;
  isSimulation = 'simulation';
  originBlocked: boolean;
  editModal = false;
  postalCodeUpdated = false;

  constructor(
    protected leadComunicationService: LeadsComunicationService,
    protected leadService: LeadService,
    protected modalsHandler: ModalsHandlerService,
    protected modalService: NgbModal,
    protected toasterService: ToastService,
    protected formBuilder: FormBuilder,
    protected styleProvider: StylesProvider,
    protected eventService: EventService,
  ) {
    this.JSON = JSON;
  }

  ngOnInit(): void {
    this.zipCode = '';
    this.modalEmitter.subscribe((leadId: string) => {
      this.simulation = leadId === this.isSimulation;
      this.leadForm = this.formBuilder.group({
        contactName: [{ value: '', disabled: this.simulation }, [Validators.required]],
        contactEmail: [{ value: '', disabled: this.simulation }, [Validators.minLength(5)]],
        contactPhone: [{ value: '', disabled: this.simulation }, [Validators.pattern('^[0-9]*$'), Validators.minLength(8)]],
        vat: [{ value: '', disabled: this.simulation }],
        ignoreVatValidation: [{ value: false, disabled: this.simulation }],
        descriptionModel: [{ value: '', disabled: this.simulation }],
        scoring: [],
        plate: [{ value: '', disabled: this.simulation }],
        address: [{ value: '', disabled: this.simulation }],
        comments: []
      }, { validator: [ValidatePhoneOrEmail, PlateFormValidator, SpanishIdValidator] });
      this.open(this.content);
      if (leadId) {
        this.getLead(leadId);
      }
    });

    this.eventService.subscribe(ChannelEvents.NEW_CHANNEL_SELECTED, item => {
      this.channelSelected = item;
    });
  }

  isValidForm(): boolean {
    if (!this.leadForm.get('contactEmail').errors.PhoneOrMailRequired &&
      !this.leadForm.get('contactPhone').errors.PhoneOrMailRequired &&
      !this.leadForm.get('contactPhone').errors.InvalidPhone &&
      !this.leadForm.get('contactEmail').errors.emailIsInvalid &&
      !this.leadForm.get('vat').errors.vatIsInvalid &&
      ((this.editModal && !this.leadForm.get('plate').errors.plateIsInvalid) || !this.editModal) &&
      !this.leadForm.get('contactName').hasError('required')) {
      return true;
    }

    return false;
  }

  processForm(dropDownBrandItems: any, dropDownChannelItems: any, dropDownDealerItems: any,
    dropDownTypeItems: any, origin?: any, postalCodeAutocomplete?: any): void {
    if (!this.isValidForm()) {
      return;
    }

    this.processPostalCode();

    const formEdited = this.isSomethingChanged(dropDownBrandItems, dropDownChannelItems, dropDownDealerItems,
      dropDownTypeItems, origin, postalCodeAutocomplete);

    this.bindForm();

    this.lead.brand = dropDownBrandItems.map(brand => brand.id)[0];
    this.lead.channel = dropDownChannelItems.map(channel => channel.id)[0];
    this.lead.dealer = dropDownDealerItems.map(dealer => dealer.id)[0];
    this.lead.type = dropDownTypeItems.map(type => type.id)[0];
    this.lead.origin = origin;

    if (formEdited || this.simulation) {
      this.launchQuery();
    }
    this.closeModal();

    if (!this.simulation) {
      this.lead = new LeadInput();
    }

    this.eventService.broadcast('resetNewLeadForm');
  }

  abstract launchQuery(): void;
  abstract closeModal(): void;
  abstract getLead(data: string): void;

  subscribeMe(observable: Observable<any>, toasterSuccess: any, toasterError: any, leadId?: string): void {
    observable.subscribe(() => {
      this.sending = true;
      this.toasterService
        .showToast(
          'success',
          toasterSuccess.title,
          toasterSuccess.body
        );
      this.leadComunicationService.launchReloadLeads(true);
      this.leadComunicationService.launchReloadMyLead(leadId);
      this.sending = false;
    }, () => {
      this.toasterService
        .showToast(
          'error',
          toasterError.title,
          toasterError.body
        );
      this.sending = false;
    });

  }

  open(content: any): void {
    this.modalService.open(content, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: this.styleProvider.getClassWhiteBrand(),
      backdrop: 'static', keyboard: false
    });
  }

  bindForm(): void {
    this.cleanValues();
    const { ignoreVatValidation, ...datosFormulario } = this.leadForm.value;
    Object.assign(this.lead, datosFormulario);
  }

  private processPostalCode() {
    if (typeof this.zipCode === 'string' && `${this.lead.postalCode} ${this.lead.location}`.trim() === this.zipCode.trim()) {
      this.postalCodeUpdated = false;
      return;
    }

    if (typeof this.zipCode === 'object'
      && this.zipCode.zipcode === this.lead.postalCode
      && `${this.zipCode.city}, ${this.zipCode.state}` === this.lead.location
    ) {
      this.postalCodeUpdated = false;
      return;
    }

    if (typeof this.zipCode === 'object') {
      this.lead.postalCode = this.zipCode.zipcode;
      this.lead.location = `${this.zipCode.city}, ${this.zipCode.state}`.trim();
      this.postalCodeUpdated = true;
      return;
    }

    isNaN(Number(this.zipCode)) ? (this.lead.location = this.zipCode, this.lead.postalCode = '')
      : (this.lead.postalCode = this.zipCode, this.lead.location = '');

    this.postalCodeUpdated = true;
  }

  private cleanValues(): void {
    const regex = new RegExp('<[^a \/]');
    if (this.leadForm.controls.comments.value) {
      this.leadForm.controls.comments.setValue(this.leadForm.controls.comments.value.replace(regex, this.replaceCustom));
    }
    if (this.leadForm.controls.descriptionModel.value) {
      this.leadForm.controls.descriptionModel.setValue(this.leadForm.controls.descriptionModel.value.replace(regex, this.replaceCustom));
    }
    if (this.leadForm.controls.contactName.value) {
      this.leadForm.controls.contactName.setValue(this.leadForm.controls.contactName.value.replace(regex, this.replaceCustom));
    }
    if (this.leadForm.controls.contactEmail.value) {
      this.leadForm.controls.contactEmail.setValue(this.leadForm.controls.contactEmail.value.replace(regex, this.replaceCustom));
    }
    if (this.leadForm.controls.vat.value) {
      this.leadForm.controls.vat.setValue(this.leadForm.controls.vat.value.replace(regex,this.replaceCustom));
    }
    if (this.leadForm.controls.address.value) {
      this.leadForm.controls.address.setValue(this.leadForm.controls.address.value.replace(regex,this.replaceCustom));
    }
    if (this.leadForm.controls.contactPhone.value) {
      this.leadForm.controls.contactPhone.setValue(this.leadForm.controls.contactPhone.value.replace(regex, this.replaceCustom));
    }

  }

  private replaceCustom(found: string): string {
    return found.replace('<', '< ');
  }

  clearModal() {
    this.zipCode = '';
  }

  private isSomethingChanged(dropDownBrandItems: any, dropDownChannelItems: any, dropDownDealerItems: any,
    dropDownTypeItems: any, origin: any, postalCodeAutocomplete?: any) {
    let formChanged = false;
    if (this.leadForm.dirty) {
      formChanged = true;
    }
    if (this.lead.brand && (dropDownBrandItems.map(brand => brand.id)[0] || this.lead.brand.id)
      && this.lead.brand.id !== dropDownBrandItems.map(brand => brand.id)[0]) {
      formChanged = true;
    }
    if (this.lead.channel && (dropDownChannelItems.map(channel => channel.id)[0] || this.lead.channel.id)
      && this.lead.channel.id !== dropDownChannelItems.map(channel => channel.id)[0]) {
      formChanged = true;
    }
    if (this.lead.dealer && (dropDownDealerItems.map(dealer => dealer.id)[0] || this.lead.dealer.id)
      && this.lead.dealer.id !== dropDownDealerItems.map(dealer => dealer.id)[0]) {
      formChanged = true;
    }

    if ((this.lead.type || this.lead.type === '') && this.lead.type !== dropDownTypeItems.map(type => type.id)[0]) {
      formChanged = true;
    }
    if ((origin || this.lead.origin) && this.lead.origin !== origin) {
      formChanged = true;
    }

    if (this.postalCodeUpdated) {
      formChanged = true;
    }

    return formChanged;
  }

  protected canSubmitOrigin() {
    this.eventService.subscribe('canSubmitOrigin', item => {
      if (!item.canCreateNew && !item.isInOrigins) {
        this.originBlocked = true;
      } else {
        this.originBlocked = false;
      }
    });
  }
}
